import { useState, useCallback, useRef, useEffect } from 'react';
import { Question, PracticeSessionResult } from '@/types/exam';
import { api } from '@/services/api';
import { mapQuestion } from '@/lib/api-adapters';
import { getContentLocale } from '@/lib/contentLocale';

export type SimulationPhase = 'loading' | 'intro' | 'running' | 'completed';

export function useSimulationSession(examId: string, questionCount: number) {
  const [phase, setPhase] = useState<SimulationPhase>('loading');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<PracticeSessionResult[]>([]);
  const [abandoned, setAbandoned] = useState(false);
  const sessionStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const questionTimeSpent = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!examId) {
      setPhase('intro');
      return;
    }

    let cancelled = false;
    setPhase('loading');

    api
      .get(`/questions/exam/${examId}/random`, {
        params: { count: questionCount, locale: getContentLocale() },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const mapped = (data as any[]).map(mapQuestion);
        setQuestions(mapped);
        setCurrentIndex(0);
        setAnswers({});
        setFlaggedQuestions({});
        setResults([]);
        questionTimeSpent.current = {};
        setPhase(mapped.length > 0 ? 'intro' : 'intro');
      })
      .catch(() => {
        if (!cancelled) {
          setQuestions([]);
          setPhase('intro');
        }
      });

    return () => { cancelled = true; };
  }, [examId, questionCount]);

  const startExam = useCallback(() => {
    sessionStartTime.current = Date.now();
    questionStartTime.current = Date.now();
    setCurrentIndex(0);
    setPhase('running');
  }, []);

  const currentQuestion = questions[currentIndex] ?? null;
  const selectedAnswer = currentQuestion ? answers[currentQuestion.id] ?? null : null;

  const selectAnswer = useCallback(
    (index: number) => {
      if (phase !== 'running' || !currentQuestion) return;
      setAnswers((prev) => ({ ...prev, [currentQuestion.id]: index }));
    },
    [phase, currentQuestion],
  );

  const accumulateCurrentQuestionTime = useCallback(() => {
    if (phase !== 'running') return;
    const current = questions[currentIndex];
    if (!current) return;
    const elapsedSeconds = Math.max(
      0,
      Math.floor((Date.now() - questionStartTime.current) / 1000),
    );
    questionTimeSpent.current[current.id] =
      (questionTimeSpent.current[current.id] ?? 0) + elapsedSeconds;
    questionStartTime.current = Date.now();
  }, [phase, questions, currentIndex]);

  const goToQuestion = useCallback(
    (index: number) => {
      if (phase !== 'running' || index < 0 || index >= questions.length) return;
      accumulateCurrentQuestionTime();
      setCurrentIndex(index);
    },
    [phase, questions.length, accumulateCurrentQuestionTime],
  );

  const toggleFlagQuestion = useCallback((questionId: string) => {
    setFlaggedQuestions((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  }, []);

  const goToNextUnanswered = useCallback(() => {
    if (phase !== 'running' || questions.length === 0) return;
    for (let step = 1; step <= questions.length; step++) {
      const nextIndex = (currentIndex + step) % questions.length;
      const question = questions[nextIndex];
      if (question && answers[question.id] === undefined) {
        accumulateCurrentQuestionTime();
        setCurrentIndex(nextIndex);
        return;
      }
    }
  }, [phase, questions, currentIndex, answers, accumulateCurrentQuestionTime]);

  const submitExam = useCallback((opts?: { abandoned?: boolean }) => {
    setAbandoned(opts?.abandoned === true);
    accumulateCurrentQuestionTime();
    const computed = questions.map((q) => {
      const selected = answers[q.id] ?? null;
      const isCorrect = selected !== null && selected === q.correctAnswer;
      return {
        questionId: q.id,
        selectedAnswer: selected ?? -1,
        correctAnswer: q.correctAnswer,
        isCorrect,
        timeSpent: questionTimeSpent.current[q.id] ?? 0,
      };
    });
    setResults(computed);
    setPhase('completed');
  }, [questions, answers, accumulateCurrentQuestionTime]);

  const answeredCount = Object.keys(answers).length;
  const totalTimeElapsed = Math.floor((Date.now() - sessionStartTime.current) / 1000);
  const correctCount = results.filter((r) => r.isCorrect).length;
  const score =
    results.length > 0
      ? Math.round((correctCount / results.length) * 100)
      : 0;
  const totalPoints = results.reduce(
    (sum, r, i) => sum + (r.isCorrect ? (questions[i]?.points ?? 1) : 0),
    0,
  );

  return {
    phase,
    questions,
    currentQuestion,
    currentIndex,
    totalQuestions: questions.length,
    selectedAnswer,
    answers,
    flaggedQuestions,
    answeredCount,
    results,
    selectAnswer,
    goToQuestion,
    goToNextUnanswered,
    toggleFlagQuestion,
    startExam,
    submitExam,
    abandoned,
    correctCount,
    score,
    totalPoints,
    totalTimeElapsed,
  };
}
